import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { initAnalytics } from '@/lib/analytics';
import { installTelemetry } from '@/lib/telemetry';
import { initTheme } from '@/lib/theme';
import './index.css';

// Error telemetry FIRST — before analytics and before React mounts, so a crash
// during startup is still reported. Feeds the same pipeline as customer sites.
installTelemetry();

// Initialize YOM analytics — auto-captures pageviews, errors, clicks, Web Vitals
initAnalytics();

// Theme: the boot script in index.html already painted the right class. This
// keeps a 'system' preference tracking the OS while the tab stays open.
initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale-while-revalidate defaults (see .claude/skills/tyashin-platform
      // → "Data fetching & CRUD" for the full rationale):
      //   • staleTime 30s — a revisit within 30s uses cache with no refetch;
      //     after that we still show cached data instantly but revalidate in
      //     the background so the UI auto-updates when data changed.
      //   • gcTime 10m — keep unused data around so back-navigation is instant.
      //   • refetchOnWindowFocus/Reconnect — auto-freshen when the user returns.
      // Override per-query where it matters (live dashboards: refetchInterval;
      // rarely-changing config: raise staleTime).
      staleTime: 30 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
    mutations: {
      // Writes are not idempotent by default — don't silently retry them.
      retry: 0,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
